import React, { useRef, useState } from 'react';
import TypeAhead, { type TypeAheadOption } from '../../../components/TypeAhead';
import quoteService from '../../../services/quote.service';
import LookupResultCard from './LookupResultCard';
import type { BillToLookupRow } from '../fixtures';

interface Props {
  /** Current bill-to display name (data.billTo). */
  value: string;
  /** Bound billto recno; > 0 shows the linked note and suppresses search.
   *  0 = a deferred / new bill-to — search is enabled so the user can reconcile
   *  it to a real bill-to. */
  linkedCode: number;
  /** User typed — the caller renames and UNLINKS (billto → 0, a new bill-to). */
  onType: (name: string) => void;
  /** User picked a real bill-to — the caller links the recno + re-denormalizes
   *  the master's address onto the quote (V1 reconcile). */
  onPick: (row: BillToLookupRow) => void;
  /** When provided, a "+ Add As New Bill To" action shows in the dropdown,
   *  called with the current search term (create mode → opens the New Bill-To modal). */
  onAddNew?: (term: string) => void;
  ariaLabel?: string;
  placeholder?: string;
}

/**
 * Debounced cms.billtos typeahead for the quote bill-to field, built on the
 * shared <TypeAhead> (free-text async mode): typing fetches matches + unlinks
 * (billto → 0), picking links the recno and re-denormalizes the master's
 * address. Lookup only — new bill-tos are created via the New Bill-To modal.
 */
const BillToLookupField: React.FC<Props> = ({ value, linkedCode, onType, onPick, onAddNew, ariaLabel, placeholder }) => {
  const [options, setOptions] = useState<TypeAheadOption[]>([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seq = useRef(0);

  const search = (term: string) => {
    if (timer.current) clearTimeout(timer.current);
    const q = term.trim();
    // A bound (real) bill-to suppresses search; the first keystroke unlinks
    // (onType → linkedCode 0), which re-enables search on the next render.
    if (q.length < 2 || linkedCode > 0) { setOptions([]); setSearching(false); return; }
    // In flight from the FIRST keystroke (debounce included) so the dropdown
    // shows "Searching…" instead of a premature "No results found"; the
    // sequence counter drops out-of-order responses from stale keystrokes.
    const mySeq = ++seq.current;
    setSearching(true);
    timer.current = setTimeout(async () => {
      try {
        const rows = await quoteService.lookupBillTo(q);
        if (mySeq !== seq.current) return;
        setOptions(rows.map((r) => ({
          value: String(r.billto),
          label: r.billtoname,
          description: [[r.city, r.state].filter(Boolean).join(', '), `#${r.billto}`].filter(Boolean).join('  ·  '),
          data: r,
        })));
      } catch {
        if (mySeq === seq.current) setOptions([]);
      } finally {
        if (mySeq === seq.current) setSearching(false);
      }
    }, 300);
  };

  // While a real bill-to is linked, show its recno inline after the name
  // (e.g. "STEELEX INC. (8206)") rather than a separate note line. Typing unlinks
  // (parent sets billto→0); strip the appended "(recno)" so it can't leak into
  // the bill-to name on the first edit.
  const display = linkedCode > 0 && value ? `${value} (${linkedCode})` : value;
  // Strip a trailing "(recno)" — or a partial one left mid-backspace, e.g.
  // "(8206", "(82" — so the appended code never leaks into the typed name. Only
  // a digit-only paren group is stripped, so a real "ACME (WEST)" is preserved.
  const handleType = (text: string) =>
    onType(linkedCode > 0 ? text.replace(/\s*\(\d*\)?$/, '') : text);

  return (
    <div className="flex-1 min-w-0">
      <TypeAhead
        freeText
        compact
        multiple={false}
        value={display}
        options={options}
        onChange={handleType}
        onSearch={search}
        onSelect={(opt) => onPick(opt.data as BillToLookupRow)}
        onCreateNew={onAddNew}
        createNewLabel={(term) => `Add As New Bill To${term.trim() ? `: "${term.trim().toUpperCase()}"` : ''}`}
        ariaLabel={ariaLabel}
        placeholder={placeholder ?? 'Search by name, Sage Tag, or #…'}
        searching={searching}
        emptyMessage={value.trim().length < 2 ? 'Type at least 2 characters to search' : undefined}
        renderOption={(opt) => {
          const r = opt.data as BillToLookupRow | undefined;
          if (!r) return null;
          return (
            <LookupResultCard
              name={r.billtoname} code={r.billto}
              address1={r.address1} address2={r.address2}
              city={r.city} state={r.state} zip={r.zip}
              details={[
                { label: 'SAGE', value: r.mas90code },
                { label: 'Sales Rep', value: r.salescsr },
                { label: 'Terms', value: r.creditTerms },
                { label: 'Credit Hold', value: r.creditHold, danger: r.creditHold === 'Y' },
                { label: 'AR Bal', value: r.arBalance },
              ]}
            />
          );
        }}
        className="w-full"
      />
    </div>
  );
};

export default BillToLookupField;
